import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MentionInput, renderWithMentions } from '@/components/MentionInput';
import { Avatar, PriorityBadge, Spinner, StatusDot, TypeBadge } from '@/components/ui';
import {
  useAddComment,
  useComments,
  useDeleteItem,
  useItem,
  useProject,
  useSprints,
  useToggleCriterion,
  useUpdateItem,
} from '@/hooks/queries';
import {
  BOARD_COLUMNS,
  PRIORITY_META,
  PRIORITY_ORDER,
  STATUS_META,
  formatMessageTime,
} from '@/lib/design';
import { useAuth } from '@/stores/auth';
import { useProjectStore } from '@/stores/project';
import type { ItemStatus } from '@/types/api';
import { useEffect, useState, type FormEvent } from 'react';

/**
 * Panel lateral de la tarea. Se abre desde el tablero y desde el backlog, y
 * concentra todo lo que se hace con un item: leerlo, marcar criterios,
 * comentarlo y cambiarle estado o responsable.
 */
export function TaskDrawer({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const projectId = useProjectStore((state) => state.activeProjectId);
  const currentUser = useAuth((state) => state.user);

  const { data: item, isLoading } = useItem(itemId);
  const { data: comments } = useComments(itemId);
  const { data: sprints } = useSprints(projectId);
  const { data: project } = useProject(projectId);
  const updateItem = useUpdateItem(projectId);
  const toggleCriterion = useToggleCriterion(itemId);
  const addComment = useAddComment(itemId);
  const deleteItem = useDeleteItem(projectId);

  const [draft, setDraft] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Borrador local de la descripcion: se escribe sin latencia y se guarda al
  // salir del campo, para no mandar una peticion por cada tecla.
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null);

  /**
   * A quien se puede mencionar: los miembros del proyecto. Mencionar a alguien
   * que no puede abrir la tarea no llevaria a ninguna parte.
   */
  const mentionables = (project?.members ?? [])
    .filter((member) => Boolean(member.user.username))
    .map((member) => ({
      id: member.user.id,
      fullName: member.user.fullName,
      avatarUrl: member.user.avatarUrl,
      username: member.user.username!,
    }));
  const description = descriptionDraft ?? item?.description ?? '';
  const descriptionDirty = descriptionDraft !== null && descriptionDraft !== (item?.description ?? '');

  // El campo crece con el texto para no obligar a desplazarse dentro de el.
  const descriptionRows = Math.max(3, description.split(String.fromCharCode(10)).length + 1);

  function saveDescription() {
    if (!item || !descriptionDirty) {
      setDescriptionDraft(null);
      return;
    }
    updateItem.mutate(
      { itemId: item.id, description: descriptionDraft },
      { onSuccess: () => setDescriptionDraft(null) },
    );
  }

  // Escape cierra el panel, como se espera de cualquier capa superpuesta.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Se llama desde el formulario (con evento) y desde el campo al pulsar Enter
  // (sin el), asi que el evento es opcional.
  function submitComment(event?: FormEvent) {
    event?.preventDefault();
    const body = draft.trim();
    if (!body) {
      return;
    }
    addComment.mutate(body, { onSuccess: () => setDraft('') });
  }

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-40 flex justify-end bg-[rgba(4,4,10,.62)]"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={item ? `${item.key}: ${item.title}` : 'Detalle de la tarea'}
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-[720px] max-w-full flex-col border-l border-line bg-surface shadow-[-16px_0_40px_rgba(0,0,0,.5)]"
      >
        {isLoading || !item ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <header className="flex h-topbar flex-none items-center gap-2.5 border-b border-line px-[18px]">
              <PriorityBadge priority={item.priority} />
              <TypeBadge type={item.type} />
              <span className="font-mono text-xs font-medium text-faint">{item.key}</span>
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(item.key)}
                className="text-[13px] text-subtle transition-colors hover:text-ink"
              >
                Copiar clave
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="flex size-[30px] items-center justify-center rounded-control border border-line-strong transition-colors hover:bg-muted-surface"
              >
                ✕
              </button>
            </header>

            <div className="flex min-h-0 flex-1">
              <div className="flex min-w-0 flex-1 flex-col gap-[18px] overflow-auto px-[22px] py-5">
                <h2 className="font-display text-[22px] font-semibold leading-[1.25] tracking-[-.015em] text-pretty">
                  {item.title}
                </h2>

                {item.isBlocked && item.blockedReason && (
                  <p className="rounded-control border border-danger-line bg-danger-soft px-3 py-2 text-[13px] text-danger">
                    <span className="font-semibold">Bloqueada:</span> {item.blockedReason}
                  </p>
                )}

                <section>
                  <div className="mb-[7px] flex items-center gap-2">
                    <h3 className="text-[11px] font-semibold uppercase tracking-[.1em] text-faint">
                      Descripcion
                    </h3>
                    {descriptionDirty && (
                      <span className="text-[11px] text-faint">sin guardar</span>
                    )}
                  </div>
                  <textarea
                    value={description}
                    onChange={(e) => setDescriptionDraft(e.target.value)}
                    onBlur={saveDescription}
                    rows={descriptionRows}
                    placeholder="Que hay que hacer, y por que."
                    aria-label="Descripcion de la tarea"
                    className="w-full resize-y rounded-control border border-transparent bg-transparent px-2 py-1.5 text-[13.5px] leading-[1.6] text-body-soft outline-none transition-colors hover:border-line focus:border-brand focus:bg-surface"
                  />
                </section>

                {item.criteria.length > 0 && (
                  <section>
                    <h3 className="mb-[9px] text-[11px] font-semibold uppercase tracking-[.1em] text-faint">
                      Criterios de aceptacion
                    </h3>
                    <ul className="flex flex-col gap-2">
                      {item.criteria.map((criterion) => (
                        <li key={criterion.id}>
                          <label className="flex cursor-pointer items-start gap-2.5">
                            <input
                              type="checkbox"
                              checked={criterion.done}
                              onChange={(e) =>
                                toggleCriterion.mutate({
                                  criterionId: criterion.id,
                                  done: e.target.checked,
                                })
                              }
                              className="sr-only"
                            />
                            <span
                              aria-hidden="true"
                              className={`mt-px flex size-[17px] flex-none items-center justify-center rounded-badge text-[11px] ${
                                criterion.done
                                  ? 'border border-success bg-success text-white'
                                  : 'border-[1.5px] border-line-dashed text-transparent'
                              }`}
                            >
                              ✓
                            </span>
                            <span
                              className={`text-[13.5px] leading-[1.45] ${
                                criterion.done ? 'text-struck line-through' : 'text-body'
                              }`}
                            >
                              {criterion.text}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <section>
                  <h3 className="mb-[11px] text-[11px] font-semibold uppercase tracking-[.1em] text-faint">
                    Actividad
                  </h3>
                  <div className="flex flex-col gap-3.5">
                    {comments?.map((comment) => (
                      <div
                        key={comment.id}
                        className={`flex gap-2.5 transition-opacity ${
                          comment.pending ? 'opacity-55' : ''
                        }`}
                      >
                        <Avatar user={comment.author} size={28} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-[13px] font-bold">{comment.author.fullName}</span>
                            <span className="text-[11px] text-fainter">
                              {comment.pending ? 'enviando…' : formatMessageTime(comment.createdAt)}
                            </span>
                          </div>
                          <p className="mt-[3px] whitespace-pre-wrap text-[13.5px] leading-[1.5] text-body text-pretty">
                            {renderWithMentions(comment.body, mentionables)}
                          </p>
                        </div>
                      </div>
                    ))}

                    {comments?.length === 0 && (
                      <p className="text-[13px] text-faint">Todavia no hay comentarios.</p>
                    )}

                    <form onSubmit={submitComment} className="flex gap-2.5">
                      <Avatar
                        user={
                          currentUser
                            ? {
                                id: currentUser.id,
                                fullName: currentUser.fullName,
                                avatarUrl: currentUser.avatarUrl,
                              }
                            : null
                        }
                        size={28}
                      />
                      {addComment.error && (
                        <p role="alert" className="text-[12.5px] text-danger-text">
                          No se pudo publicar el comentario:{' '}
                          {(addComment.error as Error).message}
                        </p>
                      )}
                      <MentionInput
                        value={draft}
                        onChange={setDraft}
                        onSubmit={() => submitComment()}
                        people={mentionables}
                        placeholder="Comenta o escribe @ para mencionar…"
                        ariaLabel="Nuevo comentario"
                        className="w-full rounded-[9px] border border-line-strong bg-surface px-3 py-2.5 text-[13.5px] outline-none focus:border-brand"
                      />
                    </form>
                  </div>
                </section>
              </div>

              <aside className="flex w-[242px] flex-none flex-col gap-4 overflow-auto border-l border-line bg-sunken px-4 py-[18px]">
                <section>
                  <div className="mb-2 flex items-center gap-2">
                    <h3 className="text-[10.5px] font-semibold uppercase tracking-[.1em] text-faint">
                      Estado
                    </h3>
                    {updateItem.isPending && (
                      <span
                        role="status"
                        aria-label="Guardando"
                        className="size-3 animate-spin rounded-full border-[1.5px] border-line border-t-brand"
                      />
                    )}
                  </div>
                  {item.sprintId ? (
                    <div className="flex flex-col gap-[5px]">
                      {BOARD_COLUMNS.map((status) => (
                        <StatusOption
                          key={status}
                          status={status}
                          active={item.status === status}
                          onSelect={() => updateItem.mutate({ itemId: item.id, status })}
                        />
                      ))}
                    </div>
                  ) : (
                    /* Las columnas pertenecen al tablero del sprint. Un item sin
                       sprint solo puede estar en el backlog, asi que se explica
                       en vez de ofrecer estados que dejarian datos incoherentes. */
                    <p className="rounded-control border border-line-strong bg-surface px-[9px] py-2 text-[12.5px] text-subtle text-pretty">
                      Esta tarea esta en el backlog. Asignala a un sprint para moverla por las
                      columnas del tablero.
                    </p>
                  )}
                </section>

                <section>
                  <h3 className="mb-2 text-[10.5px] font-semibold uppercase tracking-[.1em] text-faint">
                    Responsable
                  </h3>
                  <select
                    aria-label="Responsable"
                    value={item.assignee?.id ?? ''}
                    onChange={(e) =>
                      updateItem.mutate({ itemId: item.id, assigneeId: e.target.value || null })
                    }
                    className="w-full rounded-control border border-line-strong bg-surface px-[9px] py-2 text-[13px] outline-none focus:border-brand"
                  >
                    <option value="">Sin asignar</option>
                    {project?.members.map((member) => (
                      <option key={member.user.id} value={member.user.id}>
                        {member.user.fullName}
                      </option>
                    ))}
                  </select>
                </section>

                <section>
                  <h3 className="mb-2 text-[10.5px] font-semibold uppercase tracking-[.1em] text-faint">
                    Sprint
                  </h3>
                  <select
                    aria-label="Sprint"
                    value={item.sprintId ?? ''}
                    onChange={(e) =>
                      updateItem.mutate({ itemId: item.id, sprintId: e.target.value || null })
                    }
                    className="w-full rounded-control border border-line-strong bg-surface px-[9px] py-2 text-[13px] outline-none focus:border-brand"
                  >
                    <option value="">Backlog (sin planificar)</option>
                    {sprints
                      ?.filter((sprint) => sprint.status !== 'COMPLETED')
                      .map((sprint) => (
                        <option key={sprint.id} value={sprint.id}>
                          {sprint.name}
                          {sprint.status === 'ACTIVE' ? ' (en curso)' : ''}
                        </option>
                      ))}
                  </select>
                  <p className="mt-1.5 text-[11px] text-faint">
                    Al entrar a un sprint pasa a &laquo;por hacer&raquo; y aparece en el tablero.
                  </p>
                </section>

                <section>
                  <h3 className="mb-2 text-[10.5px] font-semibold uppercase tracking-[.1em] text-faint">
                    Prioridad
                  </h3>
                  <div className="flex flex-col gap-[5px]">
                    {PRIORITY_ORDER.map((option) => {
                      const meta = PRIORITY_META[option];
                      const active = item.priority === option;
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => updateItem.mutate({ itemId: item.id, priority: option })}
                          aria-pressed={active}
                          className="flex items-center gap-2 rounded-control border px-2.5 py-[7px] text-[13px] transition-colors"
                          style={{
                            background: active ? meta.soft : undefined,
                            borderColor: active ? meta.line : undefined,
                            color: active ? meta.color : undefined,
                            fontWeight: active ? 600 : 400,
                          }}
                        >
                          <span
                            aria-hidden="true"
                            className="size-[7px] flex-none rounded-full"
                            style={{ background: meta.color }}
                          />
                          {meta.label}
                        </button>
                      );
                    })}
                  </div>
                </section>

                <dl className="flex flex-col gap-[11px] text-[12.5px]">
                  <Field label="Puntos" value={item.storyPoints?.toString() ?? '—'} />
                  <Field
                    label="Creada"
                    value={new Intl.DateTimeFormat('es', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    }).format(new Date(item.createdAt))}
                  />
                  <Field label="Reporta" value={item.reporter.fullName} />
                </dl>

                {item.labels.length > 0 && (
                  <section>
                    <h3 className="mb-2 text-[10.5px] font-semibold uppercase tracking-[.1em] text-faint">
                      Etiquetas
                    </h3>
                    <ul className="flex flex-wrap gap-1.5">
                      {item.labels.map((label) => (
                        <li
                          key={label}
                          className="rounded-md bg-chip px-2 py-[3px] text-[11.5px] text-muted"
                        >
                          {label}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <div className="mt-auto flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      updateItem.mutate({ itemId: item.id, isBlocked: !item.isBlocked })
                    }
                    className="rounded-[9px] border border-brand-line bg-brand-tint px-3 py-2.5 text-[13px] font-semibold text-brand-strong transition-colors hover:bg-brand-tint-strong"
                  >
                    {item.isBlocked ? 'Quitar bloqueo' : 'Marcar como bloqueada'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingDelete(true)}
                    className="rounded-[9px] border border-line-strong px-3 py-2.5 text-[13px] font-semibold text-danger transition-colors hover:border-danger"
                  >
                    Eliminar tarea
                  </button>
                </div>
              </aside>
            </div>
          </>
        )}
      </div>

      {confirmingDelete && item && (
        <ConfirmDialog
          title={`Eliminar ${item.key}`}
          description={
            <>
              Se borrara <strong>{item.title}</strong> junto con sus comentarios y criterios de
              aceptacion. Esta accion no se puede deshacer.
            </>
          }
          pending={deleteItem.isPending}
          error={deleteItem.error ? (deleteItem.error as Error).message : null}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() =>
            deleteItem.mutate(item.id, {
              onSuccess: () => {
                setConfirmingDelete(false);
                onClose();
              },
            })
          }
        />
      )}
    </div>
  );
}

function StatusOption({
  status,
  active,
  onSelect,
}: {
  status: ItemStatus;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={`flex items-center gap-2 rounded-control px-2.5 py-[7px] text-[13px] transition-colors ${
        active
          ? 'bg-brand font-semibold text-brand-on'
          : 'border border-line-strong bg-surface text-body hover:border-line-hover'
      }`}
    >
      <StatusDot status={status} size={7} />
      {STATUS_META[status].label}
    </button>
  );
}

function Field({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-subtle">{label}</dt>
      <dd className="truncate font-semibold" style={color ? { color } : undefined}>
        {value}
      </dd>
    </div>
  );
}
